import { useEffect, useRef, useState } from 'react';
import { Form, ListGroup, Spinner } from 'react-bootstrap';
import { ItemBusca } from 'service/api/rest/fluxo-aprovacao-pedido-usecase';
import { getCodigoItem } from 'helper/fluxo-aprovacao-pedido-helper';

/** Debounce da busca async (evita uma requisição por tecla). */
const DEBOUNCE_MS = 300;
/** Mínimo de caracteres do código antes de disparar a busca. */
const MIN_TERMO = 1;
/** Mensagens dos estados sem resultado e de falha da busca. */
const MENSAGEM_SEM_RESULTADO = 'Nenhum item encontrado.';
const MENSAGEM_ERRO_BUSCA = 'Não foi possível buscar os itens. Tente novamente.';

type Props = {
  /** Código do item atualmente selecionado (idItem); 0 quando ainda não escolhido. */
  value: number;
  ariaLabel: string;
  disabled?: boolean;
  /** Busca async de itens por código, já vinculada à filial do pedido (injetada pela factory). */
  onBuscar?: (termo: string) => Promise<ItemBusca[]>;
  /** Dispara ao escolher uma opção da lista; preenche idItem/descrição/valor na linha. */
  onSelecionar: (item: ItemBusca) => void;
};

/**
 * Typeahead de item por código, respeitando a filial do pedido. O usuário
 * digita o código; o componente busca (async, com debounce) e lista código + descrição; ao
 * selecionar, devolve o item para a linha em edição. Sem dependência externa (react-bootstrap puro).
 */
export function ItemTypeahead({ value, ariaLabel, disabled, onBuscar, onSelecionar }: Props) {
  const [termo, setTermo] = useState<string>(value > 0 ? String(value) : '');
  const [opcoes, setOpcoes] = useState<ItemBusca[]>([]);
  const [aberto, setAberto] = useState<boolean>(false);
  /** Busca em voo — só liga DEPOIS do debounce, para não piscar por tecla. */
  const [carregando, setCarregando] = useState<boolean>(false);
  /** A última busca falhou; a lista informa o erro em vez de fingir "nenhum resultado". */
  const [erro, setErro] = useState<boolean>(false);
  /** Evita reabrir a lista logo após uma seleção (a seleção altera o termo e dispararia nova busca). */
  const ignorarBusca = useRef<boolean>(false);
  /**
   * Só busca/abre a lista após uma interação do usuário no input (foco/clique/digitação). Impede que,
   * ao entrar em edição com o campo já preenchido (value > 0), a lista abra sozinha na montagem.
   */
  const interagiu = useRef<boolean>(false);

  useEffect(() => {
    if (ignorarBusca.current) {
      ignorarBusca.current = false;
      return;
    }
    if (!interagiu.current) {
      return;
    }
    if (!onBuscar || termo.trim().length < MIN_TERMO) {
      setOpcoes([]);
      setAberto(false);
      setCarregando(false);
      setErro(false);
      return;
    }
    // `cancelado` descarta a resposta de uma busca que já foi substituída por outra (ou pela
    // desmontagem): sem isso, um retorno antigo sobrescreveria a lista do termo atual.
    let cancelado = false;
    const concluir = (itens: ItemBusca[], falhou: boolean) => {
      if (cancelado) {
        return;
      }
      setOpcoes(itens);
      setErro(falhou);
      setCarregando(false);
      setAberto(true);
    };
    const timer = setTimeout(() => {
      setCarregando(true);
      onBuscar(termo.trim())
        .then((itens) => concluir(itens, false))
        .catch(() => concluir([], true));
    }, DEBOUNCE_MS);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [termo, onBuscar]);

  function alterarTermo(novo: string) {
    interagiu.current = true;
    setTermo(novo);
  }

  /** Foco/clique é interação do usuário: reabre a lista quando já há opções carregadas. */
  function abrirPorInteracao() {
    interagiu.current = true;
    if (opcoes.length > 0) {
      setAberto(true);
    }
  }

  function selecionar(item: ItemBusca) {
    ignorarBusca.current = true;
    onSelecionar(item);
    // O campo mostra o código do fabricante (o "Sufixo" do Analítico), não a PK interna.
    setTermo(item.codigo?.trim() || String(item.idItem));
    setOpcoes([]);
    setAberto(false);
  }

  /**
   * Conteúdo da lista. Erro e "nenhum resultado" ficam na própria lista,
   * com o mesmo tom das demais telas do fluxo (`text-danger` / `text-muted`), para o usuário não
   * ficar diante de um campo mudo achando que a busca ainda está rodando.
   */
  function renderConteudoLista() {
    if (erro) {
      return <ListGroup.Item className="text-danger">{MENSAGEM_ERRO_BUSCA}</ListGroup.Item>;
    }
    if (opcoes.length === 0) {
      return <ListGroup.Item className="text-muted">{MENSAGEM_SEM_RESULTADO}</ListGroup.Item>;
    }
    return opcoes.map((item) => (
      <ListGroup.Item action key={item.idItem} onMouseDown={() => selecionar(item)}>
        {getCodigoItem(item)}
        {item.descricao ? ` — ${item.descricao}` : ''}
      </ListGroup.Item>
    ));
  }

  return (
    <div style={{ position: 'relative' }}>
      <Form.Control
        type="text"
        autoComplete="off"
        aria-label={ariaLabel}
        aria-busy={carregando}
        disabled={disabled}
        value={termo}
        onChange={(e) => alterarTermo(e.target.value)}
        onFocus={abrirPorInteracao}
        onClick={abrirPorInteracao}
        onBlur={() => setAberto(false)}
      />
      {/* Indicador de carregamento sobreposto ao campo: o usuário vê que a busca está em voo sem
          que a lista atual suma da tela (evita o "pisca" a cada resultado parcial). */}
      {carregando && (
        <Spinner
          animation="border"
          size="sm"
          role="status"
          aria-label="Buscando itens"
          data-testid={`${ariaLabel}-carregando`}
          className="text-secondary"
          style={{ position: 'absolute', right: '0.5rem', top: '50%', marginTop: '-0.5rem' }}
        />
      )}
      {aberto && (
        <ListGroup
          data-testid={`${ariaLabel}-opcoes`}
          style={{ position: 'absolute', zIndex: 5, width: '100%', maxHeight: '30vh', overflowY: 'auto' }}
        >
          {renderConteudoLista()}
        </ListGroup>
      )}
    </div>
  );
}

export default ItemTypeahead;
